/**
 * 목 API — httpApi 와 같은 인터페이스. localStorage(mockDb) 위에서 동작한다.
 *
 * 네트워크 지연을 흉내 내 로딩 상태가 실제처럼 보이게 한다.
 */
import { loadDb, saveDb } from './mockDb';
import { DEMO_GALLERY } from '../data/galleryDemo';
import { ApiError } from './errors';

const LATENCY = 260;
const wait = (ms = LATENCY) => new Promise(r => setTimeout(r, ms + Math.random() * 120));

let currentToken = null;
export const setMockToken = t => { currentToken = t; };

function db() {
  return loadDb();
}

function requireUser() {
  const d = db();
  const userId = currentToken ? d.sessions[currentToken] : null;
  const user = userId ? d.users.find(u => u.id === userId) : null;
  if (!user) throw new ApiError('Your session has expired. Please sign in again.', { status: 401, code: 'UNAUTHORIZED' });
  return user;
}

function publicUser(u) {
  if (!u) return null;
  const { password, ...rest } = u;
  return rest;
}

function makeToken() {
  return `mock.${Date.now().toString(36)}.${Math.random().toString(36).slice(2, 10)}`;
}

function nextId(kind) {
  const d = db();
  const n = d.seq[kind] || 1;
  d.seq[kind] = n + 1;
  return n;
}

function pad(n, w = 4) {
  return String(n).padStart(w, '0');
}

function pushNotification(userId, n) {
  const d = db();
  d.notifications.unshift({
    id: nextId('notification'),
    userId,
    isRead: false,
    createdAt: new Date().toISOString(),
    link: '',
    ...n
  });
}

/* ── auth ────────────────────────────────────────────────────────── */
const auth = {
  async login({ email, password }) {
    await wait();
    const d = db();
    const user = d.users.find(u => u.email.toLowerCase() === String(email).trim().toLowerCase());
    if (!user || user.password !== password) {
      throw new ApiError('Incorrect email or password.', { status: 401, code: 'BAD_CREDENTIALS' });
    }
    const token = makeToken();
    d.sessions[token] = user.id;
    saveDb();
    currentToken = token;
    return { token, user: publicUser(user) };
  },

  async signup({ name, email, password, company = '', phone = '' }) {
    await wait();
    const d = db();
    const normalized = String(email).trim().toLowerCase();
    if (d.users.some(u => u.email.toLowerCase() === normalized)) {
      throw new ApiError('An account with this email already exists.', { status: 409, code: 'DUPLICATE' });
    }
    const user = {
      id: nextId('user'),
      name: String(name).trim(),
      email: normalized,
      password,
      phone,
      company,
      address: '',
      thumbnailUrl: '',
      role: 'USER',
      dateJoined: new Date().toISOString(),
      createdAt: new Date().toISOString()
    };
    d.users.push(user);
    const token = makeToken();
    d.sessions[token] = user.id;
    pushNotification(user.id, {
      type: 'SYSTEM',
      title: `Welcome to Trinity, ${user.name.split(' ')[0]}`,
      message: 'Upload a DXF to get your first indicative quote. Your orders, certificates and nesting reports will appear here.',
      link: '/quote'
    });
    saveDb();
    currentToken = token;
    return { token, user: publicUser(user) };
  },

  async me() {
    await wait(120);
    return publicUser(requireUser());
  },

  async updateProfile(patch) {
    await wait();
    const user = requireUser();
    const allowed = ['name', 'phone', 'company', 'address', 'thumbnailUrl'];
    allowed.forEach(k => {
      if (patch[k] !== undefined) user[k] = String(patch[k]).trim();
    });
    if (!user.name) throw new ApiError('Name is required.', { status: 400, code: 'VALIDATION' });
    saveDb();
    return publicUser(user);
  },

  async changePassword({ currentPassword, newPassword }) {
    await wait();
    const user = requireUser();
    if (user.password !== currentPassword) {
      throw new ApiError('Current password is incorrect.', { status: 400, code: 'BAD_PASSWORD' });
    }
    if (!newPassword || newPassword.length < 8) {
      throw new ApiError('New password must be at least 8 characters.', { status: 400, code: 'VALIDATION' });
    }
    user.password = newPassword;
    saveDb();
    return { ok: true };
  },

  async requestPasswordReset({ email }) {
    await wait();
    /* 계정 존재 여부를 노출하지 않는다 — 항상 성공으로 응답 */
    return { ok: true, email };
  },

  async logout() {
    const d = db();
    if (currentToken) delete d.sessions[currentToken];
    currentToken = null;
    saveDb();
    return { ok: true };
  }
};

/* ── orders ──────────────────────────────────────────────────────── */
function decorateOrder(o) {
  const d = db();
  return {
    ...o,
    certificates: d.certificates.filter(c => c.orderNumber === o.orderNumber),
    nesting: d.nesting.find(n => n.orderNumber === o.orderNumber) || null
  };
}

const orders = {
  async list({ status, q, from, to, page = 1, size = 10 } = {}) {
    await wait();
    const user = requireUser();
    const d = db();
    let rows = d.orders.filter(o => o.userId === user.id);
    if (status && status !== 'ALL') {
      const set = Array.isArray(status) ? status : String(status).split(',');
      rows = rows.filter(o => set.includes(o.status));
    }
    if (q) {
      const s = q.toLowerCase();
      rows = rows.filter(
        o =>
          o.orderNumber.toLowerCase().includes(s) ||
          (o.poNumber || '').toLowerCase().includes(s) ||
          o.materials.some(m => m.materialName.toLowerCase().includes(s))
      );
    }
    if (from) rows = rows.filter(o => o.orderDate.slice(0, 10) >= from);
    if (to) rows = rows.filter(o => o.orderDate.slice(0, 10) <= to);
    rows.sort((a, b) => (a.orderDate < b.orderDate ? 1 : -1));
    const total = rows.length;
    const start = (page - 1) * size;
    return { items: rows.slice(start, start + size), total, page, size, totalPages: Math.max(1, Math.ceil(total / size)) };
  },

  async get(orderNumber) {
    await wait();
    const user = requireUser();
    const o = db().orders.find(x => x.orderNumber === orderNumber && x.userId === user.id);
    if (!o) throw new ApiError(`Order ${orderNumber} was not found.`, { status: 404, code: 'NOT_FOUND' });
    return decorateOrder(o);
  },

  async summary() {
    await wait(150);
    const user = requireUser();
    const rows = db().orders.filter(o => o.userId === user.id);
    const active = rows.filter(o => ['PENDING', 'CONFIRMED', 'IN_PRODUCTION', 'SHIPPED'].includes(o.status));
    return {
      total: rows.length,
      active: active.length,
      inProduction: rows.filter(o => o.status === 'IN_PRODUCTION').length,
      shipped: rows.filter(o => o.status === 'SHIPPED').length,
      spend: rows.filter(o => o.status !== 'CANCELLED').reduce((s, o) => s + Number(o.amount || 0), 0)
    };
  }
};

/* ── estimations (quotes) ────────────────────────────────────────── */
const estimations = {
  async list() {
    await wait();
    const user = requireUser();
    return db()
      .estimations.filter(e => e.userId === user.id)
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  },

  async get(quoteNumber) {
    await wait();
    const user = requireUser();
    const e = db().estimations.find(x => x.quoteNumber === quoteNumber && x.userId === user.id);
    if (!e) throw new ApiError(`Quote ${quoteNumber} was not found.`, { status: 404, code: 'NOT_FOUND' });
    return e;
  },

  /** 비로그인도 제출 가능 — 로그인 상태면 계정에 귀속된다. */
  async create(payload) {
    await wait(420);
    const d = db();
    let user = null;
    try {
      user = requireUser();
    } catch (e) {
      user = null;
    }
    if (!payload.items || payload.items.length === 0) {
      throw new ApiError('Add at least one DXF file before submitting.', { status: 400, code: 'VALIDATION' });
    }
    const id = nextId('estimation');
    const year = new Date().getFullYear();
    const quoteNumber = `Q-${year}-${pad(400 + id)}`;
    const total = payload.items.reduce((s, i) => s + Number(i.totalPrice || 0), 0);
    const est = {
      id,
      quoteNumber,
      userId: user ? user.id : null,
      customerName: payload.customerName || (user ? user.name : ''),
      customerEmail: payload.customerEmail || (user ? user.email : ''),
      customerPhone: payload.customerPhone || (user ? user.phone : ''),
      company: payload.company || (user ? user.company : ''),
      notes: payload.notes || '',
      status: 'SUBMITTED',
      createdAt: new Date().toISOString(),
      items: payload.items.map(i => ({
        fileName: i.fileName,
        material: i.material,
        thickness: Number(i.thickness),
        quantity: Number(i.quantity),
        width: Number(i.width || 0),
        height: Number(i.height || 0),
        cuttingLength: Number(i.cuttingLength || 0),
        pierceCount: Number(i.pierceCount || 0),
        unitPrice: Number(i.unitPrice || 0),
        totalPrice: Number(i.totalPrice || 0)
      })),
      total
    };
    d.estimations.unshift(est);
    if (user) {
      pushNotification(user.id, {
        type: 'ESTIMATION_REPLY',
        title: `Quote ${quoteNumber} received`,
        message: `${est.items.length} part${est.items.length > 1 ? 's' : ''} submitted. An estimator will confirm pricing within 24 hours.`,
        link: `/mypage/quotes/${quoteNumber}`
      });
    }
    saveDb();
    return est;
  },

  accept: acceptEstimation
};

/**
 * 견적 승인 — 주문(PENDING)을 만들고 알림을 남긴다.
 */
async function acceptEstimation(quoteNumber) {
  await wait(420);
  const user = requireUser();
  const d = db();
  const est = d.estimations.find(x => x.quoteNumber === quoteNumber && x.userId === user.id);
  if (!est) throw new ApiError(`Quote ${quoteNumber} was not found.`, { status: 404, code: 'NOT_FOUND' });
  if (!['SUBMITTED', 'REVIEWED'].includes(est.status)) {
    throw new ApiError('Only submitted or reviewed quotes can be approved.', { status: 409, code: 'STATE' });
  }
  est.status = 'APPROVED';
  const id = nextId('order');
  const year = new Date().getFullYear();
  const orderNumber = `TL-${year}-${pad(400 + id)}`;
  const byMat = new Map();
  est.items.forEach(i => {
    const k = `${i.material}|${i.thickness}`;
    const cur = byMat.get(k) || { materialName: i.material, thickness: Number(i.thickness), quantity: 0, unit: 'pcs' };
    cur.quantity += Number(i.quantity);
    byMat.set(k, cur);
  });
  const now = new Date().toISOString();
  d.orders.unshift({
    id,
    orderNumber,
    userId: user.id,
    status: 'PENDING',
    amount: est.total,
    orderDate: now,
    poNumber: '—',
    quoteNumber: est.quoteNumber,
    materials: Array.from(byMat.values()),
    timeline: [{ status: 'PENDING', at: now, note: `Order created from quote ${est.quoteNumber}` }],
    hasNesting: false,
    certificateCount: 0
  });
  est.notes = est.notes ? `${est.notes}
Converted to order ${orderNumber}` : `Converted to order ${orderNumber}`;
  pushNotification(user.id, {
    type: 'ORDER_UPDATE',
    title: `${orderNumber} received`,
    message: `Quote ${est.quoteNumber} was approved. An estimator will confirm lead time before cutting starts.`,
    link: `/mypage/orders/${orderNumber}`
  });
  saveDb();
  return { orderNumber, quoteNumber: est.quoteNumber };
}

/* ── notifications ───────────────────────────────────────────────── */
const notifications = {
  async list() {
    await wait(180);
    const user = requireUser();
    return db()
      .notifications.filter(n => n.userId === user.id)
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  },

  async unreadCount() {
    await wait(80);
    const user = requireUser();
    return db().notifications.filter(n => n.userId === user.id && !n.isRead).length;
  },

  async markRead(id) {
    await wait(80);
    const user = requireUser();
    const n = db().notifications.find(x => x.id === Number(id) && x.userId === user.id);
    if (n) n.isRead = true;
    saveDb();
    return { ok: true };
  },

  async markAllRead() {
    await wait(120);
    const user = requireUser();
    db().notifications.forEach(n => {
      if (n.userId === user.id) n.isRead = true;
    });
    saveDb();
    return { ok: true };
  }
};

/* ── materials (public) ──────────────────────────────────────────── */
const materials = {
  async list({ category, q } = {}) {
    await wait(160);
    let rows = db().materials;
    if (category) rows = rows.filter(m => m.category === category);
    if (q) {
      const s = q.toLowerCase();
      rows = rows.filter(m => m.name.toLowerCase().includes(s) || m.materialCode.toLowerCase().includes(s) || m.category.toLowerCase().includes(s));
    }
    return rows;
  }
};

/* ── certificates ────────────────────────────────────────────────── */
const certificates = {
  async list() {
    await wait();
    const user = requireUser();
    const d = db();
    const mine = new Set(d.orders.filter(o => o.userId === user.id).map(o => o.orderNumber));
    return d.certificates.filter(c => mine.has(c.orderNumber)).sort((a, b) => (a.issueDate < b.issueDate ? 1 : -1));
  },

  async byDocket(docket) {
    await wait();
    const user = requireUser();
    const d = db();
    const mine = new Set(d.orders.filter(o => o.userId === user.id).map(o => o.orderNumber));
    const s = String(docket).trim().toLowerCase();
    return d.certificates.filter(c => mine.has(c.orderNumber) && (c.docketNumber || '').toLowerCase().includes(s));
  }
};

/* ── nesting ─────────────────────────────────────────────────────── */
const nesting = {
  async orders() {
    await wait(160);
    const user = requireUser();
    const d = db();
    const withNesting = new Set(d.nesting.map(n => n.orderNumber));
    return d.orders
      .filter(o => o.userId === user.id && withNesting.has(o.orderNumber))
      .map(o => ({ orderNumber: o.orderNumber, orderDate: o.orderDate, materials: o.materials, status: o.status }));
  },

  async byOrder(orderNumber) {
    await wait();
    const user = requireUser();
    const d = db();
    const order = d.orders.find(o => o.orderNumber === orderNumber && o.userId === user.id);
    if (!order) throw new ApiError(`Order ${orderNumber} was not found.`, { status: 404, code: 'NOT_FOUND' });
    const result = d.nesting.find(n => n.orderNumber === orderNumber);
    if (!result) throw new ApiError('No nesting report has been generated for this order yet.', { status: 404, code: 'NOT_FOUND' });
    return { ...result, order: { orderNumber: order.orderNumber, orderDate: order.orderDate, status: order.status } };
  }
};

/* ── inquiries (contact) ─────────────────────────────────────────── */
const inquiries = {
  async create(payload) {
    await wait(380);
    const d = db();
    const id = nextId('inquiry');
    const ticket = `INQ-${new Date().getFullYear()}-${pad(1000 + id)}`;
    d.inquiries.push({ id, ticketNumber: ticket, createdAt: new Date().toISOString(), ...payload, attachment: payload.attachment ? payload.attachment.name : null });
    saveDb();
    return { id, ticketNumber: ticket };
  }
};

/* ── gallery (public) ────────────────────────────────────────────── */
const gallery = {
  async list() {
    await wait();
    return DEMO_GALLERY;
  }
};

const mockApi = { auth, orders, estimations, notifications, materials, certificates, nesting, inquiries, gallery };
export default mockApi;
